import type { DocumentDef } from './types.js'
import { purchaseOrder } from './purchase-order.js'
import { goodsReceipt } from './goods-receipt.js'
import { project, materialRequest } from './project-docs.js'
import {
  purchaseRequisition, requestForQuote, supplierQuotation, supplierRateContract, subcontractOrder,
  vendorEvaluation, supplierDispatch,
  workProgress, otherCost, expenseReport,
} from './purchase-docs.js'
import {
  purchaseInvoice, debitNote, billingRequest, salesInvoice, creditNote, deliveryChallan,
} from './invoice-docs.js'
import {
  materialTransfer, siteInventoryRecord, materialConsumption, warehouseTransfer,
  stockPurchaseRequest, stockAdjustment, stockAudit,
} from './inventory-docs.js'
import { toolEntry, toolRequest, toolIssue } from './tool-docs.js'

/**
 * Every document type the business raises, in menu order (docs/04).
 *
 * Adding one is this list plus one definition. No service, no table, no
 * controller, no component — that is the whole point of the framework.
 */
export const DOCUMENTS = [
  // Project
  project, materialRequest,
  // Purchase and expense: the reference's nine tiles in its order, then ours
  // (PUR-046).
  requestForQuote, supplierQuotation, supplierRateContract, purchaseOrder, subcontractOrder,
  otherCost, goodsReceipt, workProgress, expenseReport,
  purchaseRequisition, supplierDispatch, vendorEvaluation,
  // Invoice
  purchaseInvoice, debitNote, billingRequest, salesInvoice, creditNote, deliveryChallan,
  // Inventory
  materialTransfer, siteInventoryRecord, materialConsumption, warehouseTransfer,
  stockPurchaseRequest, stockAudit, stockAdjustment,
  // Tools and equipment
  toolEntry, toolRequest, toolIssue,
] as const satisfies readonly DocumentDef[]

const byKey = new Map<string, DocumentDef>(DOCUMENTS.map((d) => [d.key, d]))
const byRoute = new Map<string, DocumentDef>(DOCUMENTS.map((d) => [d.route, d]))

export const getDocumentDef = (key: string): DocumentDef | undefined => byKey.get(key)
export const getDocumentDefByRoute = (route: string): DocumentDef | undefined => byRoute.get(route)
export const documentKeys = (): string[] => [...byKey.keys()]

/** Throws rather than returning undefined — a bad doc type is a routing bug. */
export const requireDocumentDef = (key: string): DocumentDef => {
  const d = byKey.get(key)
  if (!d) throw new Error(`Unknown document type: ${key}`)
  return d
}
